import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { colorVariantClasses } from "@/theme/AppColorVariant";
import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { UserRole, UserRoleColors, UserRoleSelectOptions } from "../../models/User";
import { createUserSchema } from "@/lib/schemas/userSchemas";
import { setApiErrors } from "@/lib/form-utils";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

interface CreateUserFormValues {
  username: string;
  email: string;
  password: string;
  role: UserRole;
}

interface CreateUserModalProps {
  open: boolean;
  onClose: () => void;
  onCreateUser: (username: string, email: string, password: string, role: UserRole) => void;
}

export const CreateUserModal: React.FC<CreateUserModalProps> = ({
  open,
  onClose,
  onCreateUser,
}) => {
  const form = useForm<CreateUserFormValues>({
    defaultValues: {
      username: "",
      email: "",
      password: "",
      role: UserRole.Viewer,
    },
    resolver: zodResolver(createUserSchema),
  });

  const onSubmit = async (data: CreateUserFormValues) => {
    try {
      await onCreateUser(data.username, data.email, data.password, data.role);
      onClose();
      form.reset();
    } catch (error) {
      const rootMsg = setApiErrors(error, form.setError, { username: "username" });
      if (rootMsg) {
        form.setError("root", { message: rootMsg });
      }
    }
  };

  const handleClose = () => {
    if (!form.formState.isSubmitting) {
      form.reset();
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="min-w-[30%] flex flex-col">
        <DialogHeader>
          <DialogTitle>Create new user</DialogTitle>
        </DialogHeader>
        <Separator />
        {form.formState.errors.root && (
          <div className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
            {form.formState.errors.root.message}
          </div>
        )}
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-2 mb-1">
            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem className="space-y-1.5">
                  <FormLabel className="text-sm">Role</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className={cn("w-full", colorVariantClasses[UserRoleColors[field.value]]?.text)}>
                        <SelectValue placeholder="User role" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {UserRoleSelectOptions.map((option) => (
                        <SelectItem key={option.id} value={option.id}>
                          <span className={cn(option.color && colorVariantClasses[option.color]?.text)}>
                            {option.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem className="space-y-1.5">
                  <FormLabel className="text-sm">Username</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="space-y-1.5">
                  <FormLabel className="text-sm">Email address</FormLabel>
                  <FormControl>
                    <Input type="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => {
                const [showPw, setShowPw] = useState(false);
                return (
                  <FormItem className="space-y-1.5">
                    <FormLabel className="text-sm">Password</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input type={showPw ? "text" : "password"} className="pr-9" {...field} />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          className="absolute right-1 top-1/2 -translate-y-1/2 text-muted-foreground"
                          tabIndex={-1}
                          onClick={() => setShowPw(!showPw)}
                        >
                          {showPw ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
            <Separator />
            <div className="flex justify-end">
              <Button
                variant="secondary"
                onClick={handleClose}
                type="button"
                disabled={form.formState.isSubmitting}
                className="mr-2 w-20"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={form.formState.isSubmitting}
                className="w-20"
              >
                {form.formState.isSubmitting ? "..." : "Create"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
